import Add from 'assets/icons/Add';
import EditProcess from 'assets/icons/EditProcess';
import People from 'assets/icons/People';
import Button from 'common/components/Button';
import Divider from 'common/components/Divider';
import { SearchInput } from 'common/components/SearchInput';
import { useFilterPagination } from 'common/hooks/useFilterPagination';
import useNavigate from 'common/hooks/useNavigate';
import { useSearchTerm } from 'common/hooks/useSearchTerm';
import useTranslation from 'common/hooks/useTranslation';
import { useGetOffersQuery } from 'common/slices/offerApi.slice';
import React, { useState } from 'react';
import { ShowFeature } from 'split';

import { OfferTab } from './components/OfferTab';
import { OfferTable } from './components/OfferTable';
import { OfferTableSkeleton } from './components/OfferTableSkeleton';
import { OffersSkeleton } from './components/OffersSkeleton';

const Offers = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');

  const { t } = useTranslation('offers\\index');
  const pageSize = 30;

  const { searchTerm, debouncedSearchTerm, handleSearchTermChange } =
    useSearchTerm();

  const { pageForQuery, pageIndex, setPageIndex } = useFilterPagination({
    filters: { search: debouncedSearchTerm || '' },
  });

  const {
    data: openOffers,
    isLoading,
    isFetching,
  } = useGetOffersQuery({
    page: pageForQuery,
    size: pageSize,
    status: filter,
    orderBy: 'name',
    with_owner: false,
    with_company: false,
    search: debouncedSearchTerm,
  });

  const handlePageChange = (idx) => {
    if (idx > 0 && idx <= openOffers?.pages) {
      setPageIndex(idx);
    }
  };

  return isLoading ? (
    <div className='flex flex-col h-full w-full max-w-full p-6'>
      <OffersSkeleton />
      <OfferTableSkeleton />
    </div>
  ) : (
    <div className='flex flex-col h-full w-full max-w-full p-6'>
      <div className='flex flex-col w-full h-full'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-6'>
            <h1 className='text-3xl text-dark-liver font-semibold'>
              {t('processes')}
            </h1>
            <ShowFeature featureFlag='WEB_NO_ACTION_UI'>
              <div className='flex items-center gap-1.5'>
                <People size='20px' color='#7A7A7A' />
                <p className='text-sm text-sonic-silver'>12,034</p>
              </div>
            </ShowFeature>
            <div className='flex items-center gap-1.5'>
              <EditProcess size='20px' color='#7A7A7A' />
              <p className='text-sm text-sonic-silver'>{openOffers?.total}</p>
            </div>
          </div>
          <Button
            variant='primary'
            onClick={() => navigate('/offers/new-offer')}
          >
            <Add size='16px' color='#FFFFFF' />
            {t('add-offer')}
          </Button>
        </div>
        <div className='flex items-center justify-between mb-2 z-10'>
          <OfferTab filter={filter} setFilter={setFilter} />
          <SearchInput
            height='h-[38px]'
            placeholderText={t('search-placeholder')}
            searchTerm={searchTerm}
            handleChange={handleSearchTermChange}
          />
        </div>
        <Divider />
        {isFetching ? (
          <OfferTableSkeleton />
        ) : (
          <OfferTable
            openOffers={openOffers}
            isLoading={isLoading}
            currentTab={filter}
            pageIndex={pageIndex}
            pageSize={pageSize}
            handlePageChange={handlePageChange}
            totalPages={openOffers?.pages}
            total={openOffers?.total}
          />
        )}
      </div>
    </div>
  );
};

export default Offers;
